import { EMPTY_VALUE } from './constants'

const DAYS_PER_WEEK = 7
const DAYS_TO_KEEP_DAY_FORMAT = 21
const WEEKS_TO_KEEP_WEEK_FORMAT = 8
const MONTHS_TO_KEEP_MONTH_FORMAT = 24
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000

const QUICK_WIN_MESSAGE_IDS = {
  'qw.assign_copilot_add_ons': 'AI_RISK_REPORT.QUICK_WIN.ASSIGN_COPILOT_ADD_ONS',
  'qw.upgrade_copilot_base_skus': 'AI_RISK_REPORT.QUICK_WIN.UPGRADE_COPILOT_BASE_SKUS',
  'qw.enforce_mfa_template': 'AI_RISK_REPORT.QUICK_WIN.ENFORCE_MFA_TEMPLATE',
  'qw.enforce_block_legacy_auth': 'AI_RISK_REPORT.QUICK_WIN.ENFORCE_BLOCK_LEGACY_AUTH',
  'qw.tighten_authz_policy': 'AI_RISK_REPORT.QUICK_WIN.TIGHTEN_AUTHZ_POLICY',
  'qw.disable_sharepoint_legacy_auth': 'AI_RISK_REPORT.QUICK_WIN.DISABLE_SHAREPOINT_LEGACY_AUTH',
  'qw.remove_shared_mailbox_ga': 'AI_RISK_REPORT.QUICK_WIN.REMOVE_SHARED_MAILBOX_GA',
  'qw.fix_dynamic_group_guests': 'AI_RISK_REPORT.QUICK_WIN.FIX_DYNAMIC_GROUP_GUESTS',
  'qw.remediate_at_risk_users': 'AI_RISK_REPORT.QUICK_WIN.REMEDIATE_AT_RISK_USERS',
  'qw.add_app_owners': 'AI_RISK_REPORT.QUICK_WIN.ADD_APP_OWNERS',
  'qw.bulk_delete_pending_guests': 'AI_RISK_REPORT.QUICK_WIN.BULK_DELETE_PENDING_GUESTS',
  'qw.rotate_never_expiring_secrets': 'AI_RISK_REPORT.QUICK_WIN.ROTATE_NEVER_EXPIRING_SECRETS',
  'qw.disable_unused_app_registrations': 'AI_RISK_REPORT.QUICK_WIN.DISABLE_UNUSED_APP_REGISTRATIONS',
  'qw.set_default_sharing_link_specific': 'AI_RISK_REPORT.QUICK_WIN.SET_DEFAULT_SHARING_LINK_SPECIFIC',
}

const prettifyId = (id) => {
  if (!id) return EMPTY_VALUE
  return String(id)
    .split('.')
    .pop()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

export const getQuickWinCopy = (intl, quickWinId) => {
  const messageId = QUICK_WIN_MESSAGE_IDS[quickWinId]
  if (!messageId) {
    return {
      title: prettifyId(quickWinId),
      description: intl.formatMessage({ id: 'AI_RISK_REPORT.QUICK_WIN.UNKNOWN.DESCRIPTION' }),
    }
  }

  return {
    title: intl.formatMessage({ id: `${messageId}.TITLE` }),
    description: intl.formatMessage({ id: `${messageId}.DESCRIPTION` }),
  }
}

export const formatDate = (intl, value) => {
  if (!value) return EMPTY_VALUE
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE
  return intl.formatDate(date, { year: 'numeric', month: 'short', day: 'numeric' })
}

const getStartOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

const getFullMonthsBetween = (startDate, endDate) => {
  const yearMonthDelta = (endDate.getFullYear() - startDate.getFullYear()) * 12
  const monthDelta = endDate.getMonth() - startDate.getMonth()
  const monthCount = yearMonthDelta + monthDelta

  return endDate.getDate() < startDate.getDate() ? monthCount - 1 : monthCount
}

export const formatRelativeDate = (intl, value, { now = new Date() } = {}) => {
  if (!value) return EMPTY_VALUE

  const date = new Date(value)
  const currentDate = new Date(now)
  if (Number.isNaN(date.getTime()) || Number.isNaN(currentDate.getTime())) return EMPTY_VALUE

  const daysAgo = Math.max(0, Math.floor((getStartOfDay(currentDate) - getStartOfDay(date)) / MILLISECONDS_PER_DAY))
  if (daysAgo === 0) return intl.formatRelativeTime(0, 'day', { numeric: 'auto' })
  if (daysAgo === 1) return intl.formatRelativeTime(-1, 'day', { numeric: 'auto' })
  if (daysAgo <= DAYS_TO_KEEP_DAY_FORMAT) return intl.formatRelativeTime(-daysAgo, 'day')

  const weeksAgo = Math.floor(daysAgo / DAYS_PER_WEEK)
  if (daysAgo <= WEEKS_TO_KEEP_WEEK_FORMAT * DAYS_PER_WEEK) {
    return intl.formatRelativeTime(-weeksAgo, 'week', { numeric: weeksAgo === 1 ? 'auto' : 'always' })
  }

  const monthsAgo = Math.max(2, getFullMonthsBetween(date, currentDate))
  if (monthsAgo <= MONTHS_TO_KEEP_MONTH_FORMAT) return intl.formatRelativeTime(-monthsAgo, 'month')

  return intl.formatRelativeTime(-Math.floor(monthsAgo / 12), 'year')
}

export const formatNullable = (value) => value ?? EMPTY_VALUE
